import {
  Box,
  Grid,
  InputAdornment,
  Typography,
  styled,
  useTheme,
} from "@mui/material";

import NumberFormats from "components/NumberFormats";
import PriceInput from "components/PriceInput";
import Select from "components/Select";
import { TRADE_AMOUNT_TYPE } from "pages/BasicTrade/components/constants";
import { calculateMaximumTradeAmount } from "utils/helpers/trade";
import { useFormContext } from "react-hook-form";
import { useMemo } from "react";
import { useSelector } from "react-redux";

function TradeInput({
  coinPairSetting,
  tradeAmountMax,
  srcDecimalCount,
  destDecimalCount,
  coinMarketDetails,
  isPrice,
}) {
  const theme = useTheme();

  const tradeInsertForm = useSelector((state) => state.trade.tradeInsertForm?.data);
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const amountTypeId = watch("amountTypeId");
  const sideId = watch("sideId");
  const tradeTypeId = watch("tradeTypeId");
  const price = watch("price") || 0;

  const maxDecimalCount =
    amountTypeId === TRADE_AMOUNT_TYPE.amount.id
      ? srcDecimalCount
      : destDecimalCount;

  const handleChangeTradeType = (e) => {
    const value = e.target.value;

    setValue("amountTypeId", value);
  };

  const amountPlaceholderCoin = useMemo(() => {
    const isAmount = amountTypeId === TRADE_AMOUNT_TYPE.amount.id;

    if (isAmount) {
      return coinMarketDetails?.coinSourceName;
    }

    return coinMarketDetails?.coinDestinationName;
  }, [amountTypeId, coinMarketDetails]);

  const minimumTradeAmount = useMemo(() => {
    const minSrcValue = coinPairSetting?.minimumTradeAmountCoinSource;
    const minDestValue = coinPairSetting?.minimumTradeAmountCoinDestination;

    if (amountTypeId === TRADE_AMOUNT_TYPE.amount.id) {
      return minSrcValue;
    }

    return minDestValue;
  }, [
    amountTypeId,
    coinPairSetting?.minimumTradeAmountCoinSource,
    coinPairSetting?.minimumTradeAmountCoinDestination,
  ]);

  const maximumTradeAmount = useMemo(
    () =>
      calculateMaximumTradeAmount({
        sideId,
        amountTypeId,
        tradeTypeId,
        tradeAmountMax,
        srcDecimalCount,
        destDecimalCount,
        price,
      }),
    [
      amountTypeId,
      srcDecimalCount,
      destDecimalCount,
      tradeAmountMax,
      price,
      sideId,
      tradeTypeId,
    ]
  );

  return (
    <Grid container spacing={1}>
      {isPrice && (
        <Grid item xs={12}>
          <PriceInput
            textAlign="left"
            value={watch("price")}
            onChange={(e) => setValue("price", e)}
            error={!!errors?.price}
            helperText={errors?.price?.message}
            maxDecimal={destDecimalCount}
            placeholder={`(${coinMarketDetails?.coinDestinationName}) قیمت`}
            coinName={coinMarketDetails?.coinDestinationName}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography color="text.secondary" variant="subtitle1">
                    {coinMarketDetails?.coinDestinationName} {}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <PriceInput
          textAlign="left"
          value={watch("amount")}
          onChange={(e) => setValue("amount", e)}
          error={!!errors?.amount}
          maxDecimal={maxDecimalCount}
          helperText={errors?.amount?.message}
          placeholder={`(${amountPlaceholderCoin}) مقدار`}
          coinName={amountPlaceholderCoin}
          min={minimumTradeAmount}
          max={maximumTradeAmount}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Select
                  sx={{
                    borderRight: `1px solid ${theme.palette.modules.border}`,
                  }}
                  justifyContent="center"
                  itemValue="id"
                  itemText="name"
                  value={amountTypeId}
                  items={tradeInsertForm?.lisAmountType}
                  onChange={handleChangeTradeType}
                />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Typography color="text.secondary" variant="subtitle1">
                  {amountPlaceholderCoin}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" component="p" color="text.secondary">
          Maximum:{" "}
          <Typography
            variant="subtitle1"
            component="b"
            color="text.main"
            fontWeight={600}
          >
            {" "}
            <NumberFormats value={maximumTradeAmount} />{" "}
          </Typography>
          ( {amountPlaceholderCoin} )
        </Typography>

        <Typography variant="subtitle1" component="p" color="text.secondary">
          Minimum:{" "}
          <Typography
            fontWeight={600}
            variant="subtitle1"
            component="b"
            color="text.main"
          >
            {" "}
            <NumberFormats value={minimumTradeAmount} />{" "}
          </Typography>
          ( {amountPlaceholderCoin} )
        </Typography>
      </Grid>
    </Grid>
  );
}

export const BadgeShape = styled(Box)(({ theme }) => ({
  width: "100%",
  background: theme.palette.text.alternate,
  borderRadius: "8px",
  padding: theme.spacing(0.5),
}));

export default TradeInput;
